// Earth Shatter / Trembling Effect
// Ported from EarthShatter.tsx - identical logic

const CONFIG = {
    intensity: 15,
    waveSpeed: 3,
    rockCount: 3,
    rootDensity: 0.4,
    shakeMultiplier: 1,
    coreColor: "#331100",
    rockColor: "#3f3f46",
    glowColor: "#22c55e",
    duration: 4000,
    fadeDuration: 1000
}

const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 100, g: 100, b: 100 }
}

// --- Helper Classes ---

class Mist {
    x: number; y: number; vx: number; vy: number
    size: number; life: number; maxLife: number; opacity: number
    rotation: number; vRotation: number

    constructor(x: number, y: number, size: number) {
        this.x = x; this.y = y
        const a = Math.random() * Math.PI * 2
        const s = Math.random() * 2
        this.vx = Math.cos(a) * s
        this.vy = -0.5 - Math.random() * 1.5
        this.size = size * (1.5 + Math.random())
        this.life = 1.0
        this.maxLife = 0.005 + Math.random() * 0.015
        this.opacity = 0.3 + Math.random() * 0.3
        this.rotation = Math.random() * Math.PI * 2
        this.vRotation = (Math.random() - 0.5) * 0.05
    }

    update() {
        this.x += this.vx; this.y += this.vy
        this.rotation += this.vRotation
        this.vx *= 0.96; this.vy *= 0.98
        this.size += 0.5; this.life -= this.maxLife
    }

    draw(ctx: CanvasRenderingContext2D, sprite: HTMLCanvasElement | null, globalOpacity: number = 1.0) {
        if (this.life <= 0 || !sprite) return
        const currentOp = this.life * this.opacity * globalOpacity
        if (currentOp <= 0.001) return
        ctx.globalAlpha = currentOp
        ctx.save()
        ctx.translate(this.x, this.y)
        ctx.rotate(this.rotation)
        ctx.drawImage(sprite, -this.size, -this.size, this.size * 2, this.size * 2)
        ctx.restore()
    }
}

class Rock {
    x: number; y: number; z: number
    vx: number; vy: number; vz: number
    size: number; rotation: number; vRot: number
    grounded: boolean; poly: number[]

    constructor(x: number, y: number, intensity: number) {
        this.x = x; this.y = y; this.z = 0
        const angle = Math.random() * Math.PI * 2
        const force = Math.random() * intensity
        this.vx = Math.cos(angle) * force * 0.5
        this.vy = Math.sin(angle) * force * 0.5
        this.vz = 5 + Math.random() * 15 * (intensity * 0.5)
        this.size = 5 + Math.random() * 15
        this.rotation = Math.random() * Math.PI * 2
        this.vRot = (Math.random() - 0.5) * 0.2
        this.grounded = false

        this.poly = []
        const sides = 5 + Math.floor(Math.random() * 3)
        for (let i = 0; i < sides; i++) {
            const a = (i / sides) * Math.PI * 2
            const r = this.size * (0.6 + Math.random() * 0.8)
            this.poly.push(Math.cos(a) * r, Math.sin(a) * r)
        }
    }

    update(particles: Mist[]) {
        if (this.grounded) return
        this.x += this.vx; this.y += this.vy; this.z += this.vz
        this.vz -= 0.8
        this.rotation += this.vRot

        if (this.z <= 0) {
            const impactVelocity = Math.abs(this.vz)
            this.z = 0; this.vz = -this.vz * 0.3
            this.vx *= 0.6; this.vy *= 0.6; this.vRot *= 0.5

            if (impactVelocity > 2) {
                const count = Math.ceil(impactVelocity / 4)
                for (let i = 0; i < count; i++) particles.push(new Mist(this.x, this.y, this.size))
            }
            if (impactVelocity < 1) {
                this.grounded = true
                particles.push(new Mist(this.x, this.y, this.size * 1.5))
            }
        }
    }

    draw(ctx: CanvasRenderingContext2D, color: string) {
        const shadowScale = Math.max(0, 1 - this.z / 200)
        if (shadowScale > 0) {
            ctx.save()
            ctx.translate(this.x, this.y + 10)
            ctx.scale(1, 0.5)
            ctx.fillStyle = `rgba(0,0,0,${0.2 * shadowScale})`
            ctx.beginPath(); ctx.arc(0, 0, this.size, 0, Math.PI * 2); ctx.fill()
            ctx.restore()
        }
        ctx.save()
        const scale = 1 + (this.z / 300)
        ctx.translate(this.x, this.y - this.z)
        ctx.scale(scale, scale)
        ctx.rotate(this.rotation)
        ctx.fillStyle = color
        ctx.beginPath()
        ctx.moveTo(this.poly[0], this.poly[1])
        for (let i = 2; i < this.poly.length; i += 2) ctx.lineTo(this.poly[i], this.poly[i + 1])
        ctx.closePath(); ctx.fill()
        ctx.save()
        ctx.clip()
        ctx.fillStyle = 'rgba(255,255,255,0.15)'
        ctx.fillRect(-this.size, -this.size, this.size, this.size * 2)
        ctx.restore()
        ctx.restore()
    }
}

class RootSystem {
    points: { x: number, y: number }[]
    color: string; width: number
    currentPointIndex: number; maxPoints: number

    constructor(x: number, y: number, angle: number, size: number, color: string) {
        this.points = [{ x, y }]
        this.color = color
        this.width = size * (1.5 + Math.random() * 2.0)
        this.currentPointIndex = 1
        let cx = x; let cy = y; let cAngle = angle
        const len = (40 + Math.random() * 150) * size
        const segmentLen = 4
        this.maxPoints = Math.floor(len / segmentLen)
        for (let i = 0; i < this.maxPoints; i++) {
            cx += Math.cos(cAngle) * segmentLen
            cy += Math.sin(cAngle) * segmentLen
            this.points.push({ x: cx, y: cy })
            cAngle += (Math.random() - 0.5) * 1.2
            if (Math.random() > 0.7) cAngle *= 0.95
        }
    }

    update() {
        if (this.currentPointIndex < this.points.length) this.currentPointIndex += 2
    }

    draw(ctx: CanvasRenderingContext2D, sprite: HTMLCanvasElement | null, globalOpacity: number = 1.0) {
        if (this.currentPointIndex < 2) return
        const limit = Math.min(this.currentPointIndex, this.points.length)

        if (sprite) {
            ctx.globalCompositeOperation = 'lighter'
            ctx.globalAlpha = 0.15 * globalOpacity
            for (let i = 0; i < limit; i += 2) {
                const pt = this.points[i]
                const progress = i / this.points.length
                const spreadMultiplier = 30
                const scale = (this.width / 4) * (1 - progress * 0.7)
                if (scale <= 0.05) continue
                const size = scale * spreadMultiplier
                ctx.drawImage(sprite, pt.x - size / 2, pt.y - size / 2, size, size)
            }
            ctx.globalCompositeOperation = 'source-over'
        }

        ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = this.color
        ctx.globalAlpha = 1.0 * globalOpacity
        for (let i = 0; i < limit - 1; i++) {
            const pt1 = this.points[i]
            const pt2 = this.points[i + 1]
            const progress = i / this.points.length
            const currentWidth = Math.max(0.1, this.width * (1 - progress))
            ctx.beginPath(); ctx.moveTo(pt1.x, pt1.y); ctx.lineTo(pt2.x, pt2.y)
            ctx.lineWidth = currentWidth; ctx.stroke()
        }
    }
}

// GLOBAL SPRITE CACHE FOR TREMBLING EFFECT
const tremblingSpriteCache = new Map<string, HTMLCanvasElement>();

const getCloudSprite = (r: number, g: number, b: number): HTMLCanvasElement => {
    const key = `${r}-${g}-${b}`;
    let sprite = tremblingSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas')
        sprite.width = 128; sprite.height = 128
        const ctx = sprite.getContext('2d')!
        const cx = 64; const cy = 64
        for (let i = 0; i < 7; i++) {
            const offsetDist = Math.random() * 20
            const offsetAngle = Math.random() * Math.PI * 2
            const blobX = cx + Math.cos(offsetAngle) * offsetDist
            const blobY = cy + Math.sin(offsetAngle) * offsetDist
            const radius = 25 + Math.random() * 25
            const gr = ctx.createRadialGradient(blobX, blobY, 0, blobX, blobY, radius)
            gr.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.15)`)
            gr.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.05)`)
            gr.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`)
            ctx.fillStyle = gr
            ctx.beginPath(); ctx.arc(blobX, blobY, radius, 0, Math.PI * 2); ctx.fill()
        }
        tremblingSpriteCache.set(key, sprite);
    }
    return sprite
}

type FissurePoint = { x: number, y: number, w: number }

export type TremblingEffect = {
    kind: 'trembling'
    t: number
    sy?: number
    ty?: number
    data?: {
        waveY: number
        isRunning: boolean
        fissurePoints: FissurePoint[]
        rocks: Rock[]
        roots: RootSystem[]
        mist: Mist[]
        shake: number
        dustSprite: HTMLCanvasElement
        glowSprite: HTMLCanvasElement
    }
}

export const createTremblingEffect = (sy: number = 0, ty: number = 0): TremblingEffect => ({
    kind: 'trembling',
    t: 0,
    sy,
    ty
})

export const renderTremblingEffect = (
    eff: TremblingEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    const { width, height } = ctx.canvas
    const inverted = (eff.sy !== undefined && eff.ty !== undefined) ? eff.sy < eff.ty : false

    // Initialize
    if (!eff.data) {
        const dustSprite = getCloudSprite(120, 110, 100)
        const rgb = hexToRgb(CONFIG.glowColor)
        const glowSprite = getCloudSprite(rgb.r, rgb.g, rgb.b)

        eff.data = {
            waveY: inverted ? -100 : height + 100,
            isRunning: true,
            fissurePoints: [],
            rocks: [],
            roots: [],
            mist: [],
            shake: 0,
            dustSprite,
            glowSprite
        }
    }

    const d = eff.data
    const totalDuration = CONFIG.duration + CONFIG.fadeDuration

    // Check for end of animation
    if (eff.t > totalDuration) return false

    // Global fade
    let globalAlpha = 1.0
    if (eff.t > CONFIG.duration) {
        globalAlpha = 1 - (eff.t - CONFIG.duration) / CONFIG.fadeDuration
    }

    const triggerWave = (atY: number) => {
        const cx = width / 2
        // If inverted, width grows as we go down (atY increases).
        // If normal, width grows as we go up (height - atY increases).
        const distFromSource = inverted ? atY : (height - atY)
        const waveWidth = Math.max(20, distFromSource * 0.15)

        const offset = (Math.random() - 0.5) * 40
        d.fissurePoints.push({ x: cx + offset, y: atY, w: waveWidth })
        d.shake = 10 * CONFIG.shakeMultiplier

        const rockNum = Math.floor(Math.random() * CONFIG.rockCount) + 1
        for (let i = 0; i < rockNum; i++) {
            d.rocks.push(new Rock(cx + offset + (Math.random() - 0.5) * waveWidth, atY, CONFIG.intensity))
        }

        if (Math.random() < CONFIG.rootDensity) {
            const angle = -Math.PI / 2 + (Math.random() - 0.5) * 2
            const rootX = cx + offset + (Math.random() - 0.5) * waveWidth * 0.5
            const size = 2 + Math.random() * 4
            d.roots.push(new RootSystem(rootX, atY, angle, size, CONFIG.coreColor))
            if (Math.random() > 0.6) d.roots.push(new RootSystem(rootX, atY, angle + 0.5, size * 0.6, CONFIG.coreColor))
        }
        for (let i = 0; i < 3; i++) d.mist.push(new Mist(cx + offset + (Math.random() - 0.5) * waveWidth, atY, 15))
    }

    // Wave progression
    if (d.isRunning) {
        if (inverted) {
            d.waveY += CONFIG.waveSpeed * 10
            if (d.waveY < height + 50) {
                triggerWave(d.waveY)
                triggerWave(d.waveY - 5)
            } else {
                d.isRunning = false
                d.shake = 20 * CONFIG.shakeMultiplier
            }
        } else {
            d.waveY -= CONFIG.waveSpeed * 10
            if (d.waveY > -50) {
                triggerWave(d.waveY)
                triggerWave(d.waveY + 5)
            } else {
                d.isRunning = false
                d.shake = 20 * CONFIG.shakeMultiplier
            }
        }
    }

    // --- RENDER ---
    ctx.save()
    ctx.globalAlpha = globalAlpha

    // Shake
    if (d.shake > 0) {
        ctx.translate((Math.random() - 0.5) * d.shake, (Math.random() - 0.5) * d.shake)
        d.shake *= 0.9
        if (d.shake < 0.5) d.shake = 0
    }

    // Draw fissure (the central crack with glow)
    if (d.fissurePoints.length > 1) {
        ctx.beginPath()
        const left: { x: number, y: number }[] = []
        const right: { x: number, y: number }[] = []
        d.fissurePoints.forEach(p => {
            left.push({ x: p.x - p.w, y: p.y })
            right.push({ x: p.x + p.w, y: p.y })
        })
        ctx.moveTo(left[0].x, left[0].y)
        for (let i = 1; i < left.length; i++) ctx.lineTo(left[i].x, left[i].y)
        for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i].x, right[i].y)
        ctx.closePath()
        ctx.shadowBlur = 20; ctx.shadowColor = CONFIG.coreColor
        ctx.fillStyle = CONFIG.coreColor; ctx.fill()
        ctx.shadowBlur = 0
        ctx.strokeStyle = '#222'; ctx.lineWidth = 2; ctx.stroke()
    }

    // Draw roots
    d.roots.forEach(r => {
        r.update()
        r.draw(ctx, d.glowSprite, globalAlpha)
    })

    // Draw rocks
    d.rocks.forEach(r => {
        r.update(d.mist)
        r.draw(ctx, CONFIG.rockColor)
    })

    // Draw mist
    for (let i = d.mist.length - 1; i >= 0; i--) {
        const m = d.mist[i]
        m.update()
        m.draw(ctx, d.dustSprite, globalAlpha)
        if (m.life <= 0) {
            d.mist[i] = d.mist[d.mist.length - 1];
            d.mist.pop();
        }
    }

    ctx.restore()

    return true
}
